// @flow
import _ from 'lodash';
import FutureJob from '../protocol/FutureJob';

const debug = require('../debug/debug')('wc:upload:UploadScheduler');

/**
 * Manages a queue of uploads and schedules sequential uploads one at a time.
 * Pass it an `onQueueEmptied` callback to get notified when the queue has
 * been emptied.
 */
export default class UploadScheduler {
  _jobQueue: FutureJob[];
  _successfulJobs: FutureJob[];
  _onQueueEmptied: Function;

  constructor(onQueueEmptied: Function = _.noop) {
    this._onQueueEmptied = onQueueEmptied;
    this._jobQueue = [];
    this._successfulJobs = [];
  }

  get _notStartedJobs(): FutureJob[] {
    return this._jobQueue.filter((j) => !j.started && !j.canceled);
  }

  get _runningJobs(): FutureJob[] {
    return this._jobQueue.filter((j) => j.started && !j.done && !j.canceled);
  }

  add(job: FutureJob) {
    debug('[add()] job: ', job);
    this._jobQueue.push(job);
    this._maybeStartNextJob();
  }

  remove(job: FutureJob, removeChildren: ?boolean = false) {
    debug('[remove()] job: ', job);
    this._jobQueue = this._jobQueue.filter((j) => j !== job);

    if (removeChildren) {
      job.children.forEach((child) => this.remove(child, true));
    }

    this._maybeStartNextJob();
  }

  clear() {
    debug('[clear()]');
    this._jobQueue.forEach((j) => j.cancel());
    this._jobQueue = [];
    this._successfulJobs = [];
  }

  _maybeStartNextJob() {
    debug('[_maybeStartNextJob()] begin');
    if (this._runningJobs.length > 0) {
      debug('[_maybeStartNextJob()] running jobs exists. return.');
      return;
    }

    const notStartedJobs = this._notStartedJobs;
    if (notStartedJobs.length === 0) {
      debug('[_maybeStartNextJob()] no waiting jobs. return.');
      return;
    }

    const nextJob = notStartedJobs[0];

    this._startNextJob(nextJob);
  }

  _startNextJob(job: FutureJob) {
    debug('[_startNextJob()] starting job');
    job.start();
    job.promise.then(() => this._onJobComplete(job, true)).catch(() => this._onJobComplete(job, false));
  }

  _onJobComplete(job: FutureJob, success: boolean) {
    debug('[_onJobComplete()] args: ', { job, success });
    this.remove(job, !success);

    if (success) {
      this._successfulJobs.push(job);
    }

    if (this._jobQueue.length === 0) {
      debug('[_onJobComplete()] queue empty. Calling _onQueueEmptied()');
      this._onQueueEmptied(this._successfulJobs);
      this._successfulJobs = [];
    }
  }
}
